import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTripTypes } from '../hooks/useTripTypes'
import TripTypeForm from './TripTypeForm'
import type { TripType, CreateTripTypeInput, UpdateTripTypeInput } from '../types'

export default function TripTypes() {
  const { tripTypes, createTripType, updateTripType, deleteTripType } = useTripTypes()
  const [showForm, setShowForm] = useState(false)
  const [editingTripType, setEditingTripType] = useState<TripType | null>(null)

  const handleAddNew = () => {
    setEditingTripType(null)
    setShowForm(true)
  }

  const handleEdit = (tripType: TripType) => {
    setEditingTripType(tripType)
    setShowForm(true)
  }

  const handleFormSubmit = (data: CreateTripTypeInput | UpdateTripTypeInput) => {
    if (editingTripType) {
      updateTripType(data as UpdateTripTypeInput)
    } else {
      createTripType(data as CreateTripTypeInput)
    }
    setShowForm(false)
    setEditingTripType(null)
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingTripType(null)
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">Trip Types</h2>
        <button 
          onClick={handleAddNew}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Add New
        </button>
      </div>

      {showForm && (
        <div className="mb-6">
          <TripTypeForm
            tripType={editingTripType || undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            isEditing={!!editingTripType}
          />
        </div>
      )}

      {tripTypes.length === 0 && !showForm ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg mb-4">No trip types yet</p>
          <p className="text-gray-400">Create your first trip type to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tripTypes.map((tripType) => (
            <div
              key={tripType.id}
              className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <Link to={`/trip-types/${tripType.id}`} className="flex-1 hover:bg-gray-50 -m-4 p-4 rounded-lg transition-colors">
                  <h3 className="text-lg font-medium text-gray-900 mb-1 hover:text-blue-600">
                    {tripType.name}
                  </h3>
                  {tripType.description && (
                    <p className="text-gray-600 text-sm mb-3">
                      {tripType.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    Created {tripType.createdAt.toLocaleDateString()}
                  </p>
                </Link>
                <div className="flex gap-2 ml-4">
                  <button 
                    onClick={() => handleEdit(tripType)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => deleteTripType(tripType.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}